import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Callout } from "@radix-ui/themes";
import { fetchProductRequirementsByTemplate } from "../state/suppliers";
import {
  ProductRequirementLabelEditor,
  type ProductTemplateLabels,
} from "./ProductRequirementLabelEditor";

/** Returns true when the labels JSON is non-empty. */
export function hasLabelOverrides(
  labels: unknown,
): labels is Record<string, Record<string, string>> {
  return (
    labels != null &&
    typeof labels === "object" &&
    !Array.isArray(labels) &&
    Object.keys(labels).length > 0
  );
}

type RequirementTemplateSectionProps = {
  /** Current saved template ID (from DB item, null for create) */
  savedTemplateId: number | null;
  /** Current form template ID (from dropdown) */
  currentTemplateId: number | null;
  /** Current saved labels (from DB item) */
  savedLabels: ProductTemplateLabels;
  /** Called when labels are saved */
  onLabelsChange: (labels: ProductTemplateLabels) => void;
};

/**
 * Shared component that shows template-change warning + label editor.
 * Used by both Supplier edit dialog and MA create/edit dialog.
 */
export function RequirementTemplateSection({
  savedTemplateId,
  currentTemplateId,
  savedLabels,
  onLabelsChange,
}: RequirementTemplateSectionProps) {
  const { t } = useTranslation();

  const [labelEditorOpen, setLabelEditorOpen] = useState(false);
  const [labels, setLabels] = useState<ProductTemplateLabels>(null);
  const [descriptions, setDescriptions] = useState<Record<string, string>>({});
  const [loadingDescs, setLoadingDescs] = useState(false);
  // Local mirror of the last-saved value so re-opening shows fresh data
  const [localSavedLabels, setLocalSavedLabels] = useState<
    ProductTemplateLabels | undefined
  >(undefined);

  const templateChanged =
    savedTemplateId != null &&
    currentTemplateId != null &&
    savedTemplateId !== currentTemplateId;

  const showWarning = templateChanged && hasLabelOverrides(savedLabels);

  // The effective template for loading requirements is the current (non-changed) one
  const effectiveTemplateId = currentTemplateId ?? savedTemplateId;

  const handleOpenLabelEditor = async () => {
    if (!effectiveTemplateId) return;
    setLoadingDescs(true);
    try {
      const reqs =
        await fetchProductRequirementsByTemplate(effectiveTemplateId);
      const descMap: Record<string, string> = {};
      for (const req of reqs) {
        descMap[req.id.toString()] = req.description;
      }
      setDescriptions(descMap);

      // Use locally-saved labels if available, otherwise fall back to prop value
      const currentOverrides =
        localSavedLabels !== undefined
          ? localSavedLabels
          : (savedLabels ?? null);

      // If no overrides at all, pre-fill from the template-default labels
      if (
        currentOverrides == null ||
        Object.keys(currentOverrides).length === 0
      ) {
        const defaultLabels: Record<string, Record<string, string>> = {};
        for (const req of reqs) {
          if (
            req.labels != null &&
            typeof req.labels === "object" &&
            !Array.isArray(req.labels)
          ) {
            const localeMap = req.labels as Record<string, string>;
            if (Object.keys(localeMap).length > 0) {
              defaultLabels[req.id.toString()] = localeMap;
            }
          }
        }
        setLabels(Object.keys(defaultLabels).length > 0 ? defaultLabels : null);
      } else {
        setLabels(currentOverrides);
      }

      setLabelEditorOpen(true);
    } catch (err) {
      console.error("Error fetching product requirements:", err);
    } finally {
      setLoadingDescs(false);
    }
  };

  const handleLabelsChange = (newLabels: ProductTemplateLabels) => {
    setLocalSavedLabels(newLabels);
    onLabelsChange(newLabels);
  };

  if (!effectiveTemplateId) return null;

  return (
    <>
      {showWarning && (
        <Callout.Root color="orange" size="1">
          <Callout.Text>{t("suppliers.templateChangeWarning")}</Callout.Text>
        </Callout.Root>
      )}

      {/* Only show "Edit Labels" when the template hasn't been changed */}
      {!templateChanged && (
        <Button
          type="button"
          variant="outline"
          size="2"
          onClick={handleOpenLabelEditor}
          disabled={loadingDescs}
        >
          {loadingDescs ? t("common.loading") : t("suppliers.editLabels")}
        </Button>
      )}

      <ProductRequirementLabelEditor
        labels={labels}
        requirementDescriptions={descriptions}
        onChange={handleLabelsChange}
        open={labelEditorOpen}
        onOpenChange={setLabelEditorOpen}
      />
    </>
  );
}
